import { Activity, Coins, CreditCard, FileClock, HardDrive, KeyRound, KeySquare, LogOut, MessageCircle, MonitorDown, ShieldCheck, Users2 } from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import {
  Avatar,
  AvatarFallback,
  Badge,
  Button as ShButton,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Toaster,
  TooltipProvider,
} from '../components/shadcn/index.js';
import { useDirection, setLocale } from '../hooks/useDirection.js';
import { platformApi } from '../lib/platformApi.js';
import { usePlatformAuthStore } from '../lib/platformAuthStore.js';

/**
 * Distinct chrome from AppLayout — a persistent orange Platform pill and a
 * narrow accent rail on the sidebar so support staff can never confuse the
 * two surfaces (PRD §7). The body keeps the regular brand tokens so the
 * cards / tables inside the pages stay visually consistent with the rest
 * of the app — only the shell signals "elevated privileges".
 */
interface NavItem {
  to: string;
  labelKey: string;
  icon: ComponentType<{ className?: string }>;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/platform/tenants', labelKey: 'nav.tenants', icon: Users2 },
  { to: '/platform/payments', labelKey: 'nav.payments', icon: CreditCard },
  { to: '/platform/admins', labelKey: 'nav.admins', icon: ShieldCheck },
  { to: '/platform/currencies', labelKey: 'nav.currencies', icon: Coins },
  { to: '/platform/terminal', labelKey: 'nav.terminal', icon: MonitorDown },
  { to: '/platform/whatsapp', labelKey: 'nav.whatsapp', icon: MessageCircle },
  { to: '/platform/otp-service', labelKey: 'nav.otpService', icon: KeySquare },
  { to: '/platform/storage', labelKey: 'nav.storage', icon: HardDrive },
  { to: '/platform/audit', labelKey: 'nav.audit', icon: FileClock },
];

export function PlatformLayout({ children }: { children: ReactNode }): JSX.Element {
  const { t, i18n } = useTranslation('platform');
  const { t: tc } = useTranslation();
  const dir = useDirection();
  const location = useLocation();
  const navigate = useNavigate();
  const admin = usePlatformAuthStore((s) => s.admin);

  const onLogout = async () => {
    try {
      await platformApi().post('/auth/logout', {});
    } finally {
      usePlatformAuthStore.getState().clear();
      navigate('/platform/login');
    }
  };

  const toggleLang = () => setLocale(i18n.language.startsWith('ar') ? 'en' : 'ar');

  const initials = (admin?.fullName ?? admin?.email ?? 'PA')
    .split(/\s+/)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('') || 'PA';

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className="relative isolate flex min-h-screen flex-col bg-bg-subtle text-fg"
        dir={dir}
      >
        {/* Sticky topbar with the persistent Platform pill */}
        <header className="sticky top-0 z-30 border-b border-warning/40 bg-bg/80 backdrop-blur supports-[backdrop-filter]:bg-bg/60">
          <div className="flex h-14 items-center justify-between gap-4 px-6">
            <div className="flex items-center gap-3">
              <Badge
                variant="warning"
                className="rounded-md px-2 py-1 text-[0.65rem] font-bold uppercase tracking-[0.12em]"
              >
                {t('badge')}
              </Badge>
              <span className="hidden text-sm text-fg-muted md:inline">
                {t('header.subtitle')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <ShButton
                variant="ghost"
                size="sm"
                onClick={toggleLang}
                aria-label={tc('actions.switchLanguage')}
                className="h-8 px-2 text-xs font-medium uppercase"
              >
                {i18n.language.startsWith('ar') ? 'EN' : 'AR'}
              </ShButton>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-2 rounded-full px-1 py-1 outline-none focus-visible:ring-2 focus-visible:ring-warning/40"
                    aria-label={tc('actions.userMenu')}
                  >
                    <Avatar className="h-8 w-8 bg-warning text-fg-inverse">
                      <AvatarFallback className="bg-warning text-fg-inverse">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="flex flex-col gap-0.5">
                    <span className="text-sm font-semibold">{admin?.fullName ?? '—'}</span>
                    <span className="text-xs font-normal text-fg-muted">{admin?.email}</span>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild className="gap-2">
                    <Link to="/platform/change-password">
                      <KeyRound className="h-4 w-4" aria-hidden />
                      {tc('actions.changePassword', { defaultValue: 'Change password' })}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={onLogout} className="gap-2 text-danger">
                    <LogOut className="h-4 w-4" aria-hidden />
                    {tc('actions.signOut')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <div className="grid flex-1 grid-cols-1 lg:grid-cols-[15rem_1fr]">
          {/* Sidebar — narrow orange accent on the inner edge keeps the
              "platform mode" signal even after the user scrolls past the
              sticky topbar. */}
          <aside
            className="hidden border-e border-border bg-bg lg:flex lg:flex-col"
            style={{ boxShadow: 'inset -2px 0 0 var(--color-warning)' }}
          >
            <div className="flex items-center gap-2 px-5 py-5">
              <div className="flex h-8 w-8 items-center justify-center rounded-md bg-warning text-fg-inverse">
                <ShieldCheck className="h-4 w-4" aria-hidden />
              </div>
              <span className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
                {t('header.title')}
              </span>
            </div>
            <nav className="flex flex-col gap-0.5 px-3 pb-4" aria-label="Platform">
              {NAV_ITEMS.map((item) => {
                const active =
                  location.pathname === item.to ||
                  location.pathname.startsWith(`${item.to}/`);
                const Icon = item.icon;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={[
                      'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition',
                      active
                        ? 'bg-warning/15 font-medium text-warning'
                        : 'text-fg hover:bg-bg-subtle',
                    ].join(' ')}
                  >
                    <Icon
                      className={`h-4 w-4 ${active ? 'text-warning' : 'text-fg-muted'}`}
                      aria-hidden
                    />
                    {t(item.labelKey)}
                  </Link>
                );
              })}
            </nav>
            <div className="mt-auto px-5 py-4 text-[0.7rem] text-fg-muted">
              <div className="flex items-center gap-2">
                <Activity className="h-3 w-3" aria-hidden />
                <span>{t('header.elevated')}</span>
              </div>
            </div>
          </aside>

          <main className="px-6 py-6 lg:px-10">{children}</main>
        </div>

        <Toaster position="top-right" richColors closeButton />
      </div>
    </TooltipProvider>
  );
}
